import React, { createContext, useContext, useState } from "react"
import {
	MobergColumn,
	MobergRow,
} from "../../../../../Moberg"
import { SourceHeatmapModalities } from "./SourceHeatmapModalities"
import { AnalyticHeatmapModalities } from "./AnalyticHeatmapModalities"
import { ConfigureWindowModalContext } from "../ConfigureWindowModal"
import { CompositePartTraceConfigJSON, HeatmapTraceConfig, HeatmapTraceConfigJSON, RenderStrategy, TraceConfigJSON, UpdateTraceOptions } from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { ColorSpectrum } from "../../../../../Pages/Data/Visualize/DataReview/Types/ColorSpectrum"
import { TabCount } from "../LineGraphModalities/TabCount"
import { applyModalitySpecificDefaults, withDefaultNumericDisplay } from "../../../../../Constants/LabelSettings"

type HeatmapGraphModalitiesContextValue = {
	activeSourceTraces: HeatmapTraceConfig[]
	activeAnalyticTraces: HeatmapTraceConfig[]
	handleColorSpectrumChange: (colorSpectrum: ColorSpectrum, traceConfig: HeatmapTraceConfigJSON) => void
	handleValidationErrorChange: (key: string, error: string | undefined) => void
	handleDelete: (id: string) => void
	updateTrace: (traceId: string, config: HeatmapTraceConfigJSON, options?: UpdateTraceOptions) => void
}

export const HeatmapGraphModalitiesContext = createContext<HeatmapGraphModalitiesContextValue>({} as HeatmapGraphModalitiesContextValue)

export const HeatmapGraphModalities: React.FC = () => {
	const tabs = ["Source data", "Analytics"]
	const [selectedTab, setSelectedTab] = useState(tabs[0])

	const { activeSourceTraces, activeAnalyticTraces, updateGraphProperty, setValidationErrors, removeTrace } = useContext(ConfigureWindowModalContext)

	const TabContent = () => {
		switch(selectedTab) {
			case tabs[0]:
				return <SourceHeatmapModalities />
			case tabs[1]:
				return <AnalyticHeatmapModalities />
			default:
				throw new Error("Tab section not implemented! " + selectedTab)
		}
	}

	const handleValidationErrorChange = (key: string, error: string | undefined) => {
		setValidationErrors(previous => {
			const copy = new Map(previous)

			if (copy.has(key) && error === undefined) {
				copy.delete(key)
			} else if (error !== undefined) {
				copy.set(key, error)
			}

			return copy
		})
	}

	const updateTrace = (traceId: string, config: HeatmapTraceConfigJSON, options?: UpdateTraceOptions) => {
		const defaults = { isVisible: true }

		updateGraphProperty("traces", (previous: TraceConfigJSON[]) => previous.map(trace => {

            if (traceId === trace.id) {
        		let newTrace = { ...defaults, ...trace, ...config, id: traceId }

				if (options?.useModalitySpecificDefaults) {
					newTrace = withDefaultNumericDisplay(applyModalitySpecificDefaults(newTrace, config.rawDataKey, RenderStrategy.HEATMAP), trace.summaryValueConfig)
				}

				if (config.isCompositePart && config.compositeIndex !== undefined) {
					(newTrace as CompositePartTraceConfigJSON).compositeIndex = config.compositeIndex
				}
		
				// This optional property should be removed if the the modality changed and the new trace does not have an analysis.
				if (config.name && !config.onDemandAnalysis) {
					newTrace.onDemandAnalysis = undefined
				}

				return newTrace
			}

            return trace
        }))
    }

	const handleColorSpectrumChange = (colorSpectrum: ColorSpectrum, traceConfig: HeatmapTraceConfigJSON) => {
		updateTrace(traceConfig.id, {...traceConfig, colorSpectrum })
	}

	const handleDelete = (id: string) => {
		removeTrace(id)

		// When we delete the component, we have to remove the validation errors that belonged to these components.
		handleValidationErrorChange(`${id}-lowerBound`, undefined)
		handleValidationErrorChange(`${id}-upperBound`, undefined)
	}
	
	return (
		<MobergColumn gap="16px">
			<div style={{ borderBottom: "1px solid var(--color-border-light)" }}>
				<MobergRow gap="24px" style={{ padding: "0 4px" }}>
					{tabs.map(tab => (
						<div
							key={tab}
							onClick={() => setSelectedTab(tab)}
							style={{
								cursor: "pointer",
								padding: "8px 4px",
								fontWeight: tab === selectedTab ? 600 : 400,
								color: tab === selectedTab ? "var(--color-accent-interactive)" : "var(--text-secondary-color)",
								borderBottom: tab === selectedTab ? "2px solid var(--color-accent-interactive)" : "2px solid transparent",
								transition: "color 0.15s, border-bottom 0.15s",
								fontSize: 14,
								background: "none",
								outline: "none",
								display: "flex",
								alignItems: "center",
								gap: "6px",
								marginBottom: "-1px",
							}}
						>
							<span>{tab}</span>
							<TabCount tab={tab} />
						</div>
					))}
				</MobergRow>
			</div>

			<HeatmapGraphModalitiesContext.Provider value={{ 
				activeSourceTraces: activeSourceTraces as HeatmapTraceConfig[], 
				activeAnalyticTraces: activeAnalyticTraces as HeatmapTraceConfig[], 
				handleDelete, handleColorSpectrumChange, handleValidationErrorChange, updateTrace }}>
				{TabContent()}
			</HeatmapGraphModalitiesContext.Provider>

		</MobergColumn>
	)
}
